import React from 'react';
import { StandardSection, createEntitySectionConfig } from '../common';
import type { TableColumn } from '../common';
import type { DailyActivity } from '../../types';
import { dailyActivityFormFields } from '../../config/formFields';

interface ActivitiesSectionProps {
  data: {
    dailyActivities: DailyActivity[];
  };
  operations: {
    handleDailyActivitySubmit: (data: Record<string, any>) => Promise<void>;
    handleDailyActivityToggle: (item: DailyActivity) => Promise<void>;
    handleDailyActivityDelete: (item: DailyActivity) => Promise<void>;
    handleDailyActivityEdit: (item: DailyActivity, key: string, newValue: any) => Promise<void>;
  };
  userPermissions: {
    canEditReports: boolean;
    canDeleteReports: boolean;
  };
}

export const ActivitiesSection: React.FC<ActivitiesSectionProps> = ({ data, operations, userPermissions }) => {
  // Table column configurations - simplified to just show activity names
  const dailyActivityColumns: TableColumn<DailyActivity>[] = [
    {
      key: 'name',
      title: 'Actividad',
      width: '80%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleDailyActivityEdit
    }
  ];

  // Quick Setup Handler for drilling activities from Excel guide
  const handleActivitiesQuickSetup = async () => {
    if (window.confirm('¿Cargar actividades de perforación desde la guía Excel? Esta acción agregará las 14 actividades estándar.')) {
      // Exact list from "Guía de carga para el Reporte CdS.md" lines 15-29
      const drillingActivities = [
        'Perfora',
        'Cementa',
        'Entuba',
        'Termina',
        'Maniobra',
        'Saca herramienta',
        'Baja herramienta',
        'Paro gremial',
        'Cambia trépano',
        'Equipo parado',
        'Parado día festivo',
        'Otros',
        'DTM',
        'Skidding'
      ];

      try {
        for (let i = 0; i < drillingActivities.length; i++) {
          const activityName = drillingActivities[i];
          await operations.handleDailyActivitySubmit({
            name: activityName
          });
        }
        alert('✅ Actividades de perforación cargadas exitosamente');
      } catch (error) {
        console.error('Error cargando actividades:', error);
        alert('❌ Error cargando actividades. Ver consola para detalles.');
      }
    }
  };

  return (
    <>
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h2>📝 Actividades Diarias</h2>
        <p style={{ color: '#666', marginTop: '10px' }}>
          Gestiona el catálogo de actividades diarias para reportes operativos y seguimiento de tareas
        </p>
      </div>

      {/* Activities Section */}
      <StandardSection
        config={createEntitySectionConfig(
          'Actividades Diarias',
          '📝',
          data.dailyActivities,
          dailyActivityFormFields,
          dailyActivityColumns,
          operations.handleDailyActivitySubmit,
          operations.handleDailyActivityToggle,
          operations.handleDailyActivityDelete,
          {
            description: 'Define las actividades diarias de perforación para selección en reportes. Solo se usa como referencia para indicar qué actividad se realizó cada día.',
            quickSetup: {
              title: 'Carga Rápida de Actividades',
              description: 'Carga las 14 actividades de perforación de la guía Excel: Perfora, Cementa, Entuba, Termina, Maniobra, etc.',
              buttonLabel: '📝 Cargar Lista de Actividades de Perforación',
              onQuickSetup: handleActivitiesQuickSetup
            }
          }
        )}
      />

      {/* Simple Activities Summary */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#f3e5f5',
        borderRadius: '12px',
        border: '2px solid #9C27B0'
      }}>
        <h3 style={{ color: '#7B1FA2', marginBottom: '15px' }}>📊 Resumen de Actividades</h3>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: data.dailyActivities.length > 0 ? '#4CAF50' : '#999'
          }}>
            {data.dailyActivities.length}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            actividades de perforación cargadas
          </div>
        </div>

        {data.dailyActivities.length === 0 && (
          <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            No hay actividades registradas. Usa la carga rápida para comenzar.
          </div>
        )}
      </div>
    </>
  );
};